import api, { queryClient } from "@app/config/api/api";
import type { components } from "@generated/openapi";
import { useQuery } from "@tanstack/react-query";

export type ResearchSession = components["schemas"]["ResearchSession"];
export type ResearchSessionStatus = ResearchSession["status"];
export type ResearchReportListItem = components["schemas"]["ResearchReportListItem"];
export type ResearchReport = components["schemas"]["ResearchReport"];

// experiment_id is a required query param on the sessions list endpoint, so api.queryOptions can't
// build a paramless key here (unlike /datasets/ or /experiments/); invalidate by the [method, path]
// prefix react-query matches against instead, covering every experiment_id variant in the cache.
export const invalidateResearchSessions = () =>
  queryClient.invalidateQueries({ queryKey: ["get", "/research/sessions/"] });

export const invalidateResearchReports = () =>
  queryClient.invalidateQueries({ queryKey: ["get", "/research/reports/"] });

export const invalidateAttachedReportIds = () =>
  queryClient.invalidateQueries({ queryKey: ["get", "/research/reports/attached-ids"] });

// throwOnError:false so a failed fetch renders an inline Alert in the tab/modal rather than
// escaping to the route error boundary (the queryClient default throws); mirrors the agent-session
// messages query, which opts out for the same reason.
export const useResearchSessions = (experimentId: string) =>
  api.useQuery(
    "get",
    "/research/sessions/",
    { params: { query: { experiment_id: experimentId } } },
    { throwOnError: false }
  );

export const useResearchReports = (originExperimentId?: string, search?: string, enabled: boolean = true) =>
  api.useQuery(
    "get",
    "/research/reports/",
    { params: { query: { origin_experiment_id: originExperimentId, search: search || undefined } } },
    { enabled, throwOnError: false }
  );

export const useAttachedReportIds = (experimentId: string, enabled: boolean = true) =>
  api.useQuery(
    "get",
    "/research/reports/attached-ids",
    { params: { query: { experiment_id: experimentId } } },
    { enabled, throwOnError: false }
  );

export const researchReportOptions = (reportId: string) =>
  api.queryOptions("get", "/research/reports/{report_id}", { params: { path: { report_id: reportId } } });

export const useResearchReport = (reportId: string | null) =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useQuery({ ...researchReportOptions(reportId!), enabled: Boolean(reportId), throwOnError: false });

export const useCancelResearch = () =>
  api.useMutation("post", "/research/sessions/{session_id}:cancel", {
    onSuccess: invalidateResearchSessions
  });

export const useCleanSession = () =>
  api.useMutation("delete", "/research/sessions/{session_id}", {
    onSuccess: invalidateResearchSessions
  });

export const useAttachReport = () =>
  api.useMutation("post", "/research/reports/{report_id}:attach", {
    onSuccess: () => {
      void invalidateResearchReports();
      void invalidateAttachedReportIds();
    }
  });

export const useLaunchResearchDev = () =>
  api.useMutation("post", "/research/sessions/", {
    onSuccess: invalidateResearchSessions
  });
